import { zodResolver } from '@hookform/resolvers/zod'
import { AvailableLanguages } from '@repo/toolkit/language-types'
import FilesSelection from '@repo/ui/files-selection'
import InnerBox from '@repo/ui/inner-box'
import { TFunction } from 'i18next'
import { useForm } from 'react-hook-form'
import { z, ZodType } from 'zod'

import { useNativeCountryField } from '../../../lib/hooks/use-native-country-name'
import useTranslate from '../../../lib/hooks/use-translate'
import { TeachingHallInput } from '../../../lib/types/models/ol/teaching-hall/teaching-hall-input'
import { BaseFormProps } from '../../../lib/types/utils'
import { getSubmitAction } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { FILE_VALIDATION } from '../../../lib/utils/validations/file.validation'
import { createNumberSchema } from '../../../lib/utils/validations/int-array-validation'
import { PHONE_NUMBER_SCHEMA } from '../../../lib/utils/validations/phone-number.validation'
import { createStringSchema } from '../../../lib/utils/validations/string-validation'
import { FORM_MODE } from '../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../elements/forms/form'
import { ControlledAdressInput } from '../../elements/inputs/address-input'
import ControlledInput from '../../elements/inputs/controlled-input'
import ControlledPhoneInput from '../../elements/inputs/controlled-phone-input'
import ValidationButtons from '../../elements/validation-buttons'

const formSchema = (t: TFunction<AvailableLanguages, undefined>): ZodType<TeachingHallInput> =>
  z.object({
    name: createStringSchema('a Name of the teaching hall', { language: 'en' }),
    numberOfClassrooms: createNumberSchema('Number of classrooms', {
      isPositive: true,
      isInteger: true,
      language: 'en'
    }),
    address1: createStringSchema('Address', { language: 'en' }),
    address2: createStringSchema('Complementary address', { nullish: true, language: 'en' }),
    city: createStringSchema('a City', { language: 'en' }),
    postalCode: createStringSchema('a Postal code', { language: 'en' }),
    country: createStringSchema('a Country', { language: 'en' }),
    contactEmail: createStringSchema('Email', { isEmail: true, language: 'en' }),
    contactFirstName: createStringSchema('First name', { language: 'en' }),
    contactLastName: createStringSchema('Last name', { language: 'en' }),
    contactPhone: PHONE_NUMBER_SCHEMA(t),
    files: z.array(FILE_VALIDATION()).optional()
  })

const TeachingHallForm = ({
  defaultValues,
  ...validationButtonProps
}: BaseFormProps<TeachingHallInput, true>) => {
  const { t } = useTranslate()
  const form = useForm<TeachingHallInput>({
    resolver: zodResolver(formSchema(t)),
    defaultValues,
    mode: FORM_MODE
  })
  useNativeCountryField(form, 'country')

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}
        className="bg-white rounded-2xl p-5"
      >
        <div>
          <div className="flex justify-between gap-5">
            <div className="flex-1 flex flex-col gap-4">
              {/* Details */}
              <InnerBox className="rounded-xl" title="Details">
                <div className="h-4" />
                <div className="flex flex-col gap-3">
                  <div className="flex justify-between gap-3">
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem className="flex-1">
                          <FormControl>
                            <ControlledInput
                              {...field}
                              required
                              label="Name of the teaching hall"
                              type="text"
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="numberOfClassrooms"
                      render={({ field }) => (
                        <FormItem className="flex-1">
                          <FormControl>
                            <ControlledInput
                              {...field}
                              required
                              label="Number of classrooms"
                              type="number"
                              allowNegative={false}
                              isControllable
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="address1"
                    render={({ field }) => (
                      <FormItem className="flex-1">
                        <FormControl>
                          <ControlledAdressInput
                            nativeCountryNames
                            {...field}
                            required
                            label={'Address'}
                            onDetailsChange={(placeDetails) => {
                              form.setValue('address1', placeDetails.address, {
                                shouldValidate: true
                              })
                              if (placeDetails.postalCode)
                                form.setValue('postalCode', placeDetails.postalCode, {
                                  shouldValidate: true
                                })
                              form.setValue('city', placeDetails.city, { shouldValidate: true })
                              form.setValue('country', placeDetails.country, {
                                shouldValidate: true
                              })
                              form.trigger(['address1', 'city', 'country'])
                            }}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="address2"
                    render={({ field }) => (
                      <FormItem className="flex-1">
                        <FormControl>
                          <ControlledInput {...field} label="Address complement" type="text" />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  <div className="flex justify-between gap-3">
                    <FormField
                      control={form.control}
                      name="city"
                      render={({ field }) => (
                        <FormItem className="flex-1">
                          <FormControl>
                            <ControlledInput {...field} required label="Town" type="text" />
                          </FormControl>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="postalCode"
                      render={({ field }) => (
                        <FormItem className="flex-1">
                          <FormControl>
                            <ControlledInput {...field} required label="Zip code" type="text" />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="country"
                    render={({ field }) => (
                      <FormItem className="flex-1">
                        <FormControl>
                          <ControlledInput {...field} required label="Country" type="text" />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>
              </InnerBox>

              {/* Contact Information */}
              <InnerBox className="rounded-xl" title="Contact Information">
                <div className="h-4" />
                <div className="flex flex-col gap-3">
                  <div className="flex justify-between gap-3">
                    <FormField
                      control={form.control}
                      name="contactLastName"
                      render={({ field }) => (
                        <FormItem className="flex-1">
                          <FormControl>
                            <ControlledInput {...field} required label="NAME" type="text" />
                          </FormControl>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="contactFirstName"
                      render={({ field }) => (
                        <FormItem className="flex-1">
                          <FormControl>
                            <ControlledInput {...field} required label="First name" type="text" />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="contactEmail"
                    render={({ field }) => (
                      <FormItem className="flex-1">
                        <FormControl>
                          <ControlledInput
                            {...field}
                            required
                            label="Contact email"
                            type="email"
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="contactPhone"
                    render={({ field }) => (
                      <FormItem className="flex-1">
                        <FormControl>
                          <ControlledPhoneInput
                            {...field}
                            required
                            label="Phone number"
                            placeholder="Phone number"
                            defaultCountry="FR"
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>
              </InnerBox>
            </div>

            <div className="flex-1 flex flex-col gap-4">
              {/* Photos of the teaching hall */}

              <FormField
                control={form.control}
                name="files"
                render={({ field, fieldState }) => (
                  <FormItem className="flex-1">
                    <FormControl>
                      <FilesSelection
                        locale="en"
                        hasError={!!fieldState.error}
                        options={{ displayDetails: false }}
                        title="Photos of the teaching hall"
                        files={field.value ?? []}
                        onFilesChange={(files) =>
                          form.setValue('files', files, { shouldValidate: true })
                        }
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
          </div>
        </div>

        <div>
          <ValidationButtons {...validationButtonProps} />
        </div>
      </form>
    </Form>
  )
}

export default TeachingHallForm
